import { useState, useEffect, useCallback } from "react";
import api from "../lib/adminApi";
import { useToast } from "../components/Toast";
import { Globe, Pencil, X, Loader2, Check, ToggleLeft, ToggleRight, Trash2 } from "lucide-react";

const COUNTRY_FLAGS = { IN: "🇮🇳", AE: "🇦🇪", SA: "🇸🇦", QA: "🇶🇦", BH: "🇧🇭", KW: "🇰🇼", OM: "🇴🇲", OTHER: "🌐" };
const SUPPORTED_CURRENCIES = ["INR", "AED", "SAR", "QAR", "OMR", "BHD", "KWD", "USD"];

export default function CountrySettingsPage() {
  const toast = useToast();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get("/country-settings");
      setRows(res.data.items || res.data || []);
    } catch { toast.error("Failed to load country settings"); }
    finally { setLoading(false); }
  }, [toast]);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="p-6 md:p-8 max-w-6xl mx-auto glass-animate" data-testid="country-settings-page">
      <h1 className="text-2xl font-extrabold text-[#0B0F1A] tracking-tight mb-1">Country & Region Settings</h1>
      <p className="text-sm text-[#5B617A] mb-6">{rows.length} regions configured. Manage currencies, tax, and available plans per country.</p>

      <div className="glass-card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="country-settings-table">
            <thead>
              <tr className="bg-white/40 border-b border-[#7C5CFF]/[0.08] text-left">
                <th className="px-3 py-2.5 text-[10px] font-semibold text-[#5B617A] uppercase tracking-wider">Flag</th>
                <th className="px-3 py-2.5 text-[10px] font-semibold text-[#5B617A] uppercase tracking-wider">Code</th>
                <th className="px-3 py-2.5 text-[10px] font-semibold text-[#5B617A] uppercase tracking-wider">Name</th>
                <th className="px-3 py-2.5 text-[10px] font-semibold text-[#5B617A] uppercase tracking-wider">Currency</th>
                <th className="px-3 py-2.5 text-[10px] font-semibold text-[#5B617A] uppercase tracking-wider">Tax</th>
                <th className="px-3 py-2.5 text-[10px] font-semibold text-[#5B617A] uppercase tracking-wider">Rate</th>
                <th className="px-3 py-2.5 text-[10px] font-semibold text-[#5B617A] uppercase tracking-wider">Active Plans</th>
                <th className="px-3 py-2.5 text-[10px] font-semibold text-[#5B617A] uppercase tracking-wider">Signup</th>
                <th className="px-3 py-2.5 text-[10px] font-semibold text-[#5B617A] uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={9} className="text-center py-12"><Loader2 className="w-5 h-5 animate-spin text-[#7C5CFF] mx-auto" /></td></tr>
              ) : rows.map(r => (
                <tr key={r.code} className="border-b border-[#7C5CFF]/[0.04] glass-row-hover transition-colors" data-testid={`country-row-${r.code}`}>
                  <td className="px-3 py-3 text-lg">{COUNTRY_FLAGS[r.code] || "🌐"}</td>
                  <td className="px-3 py-3 font-mono font-bold text-[#0B0F1A]">{r.code}</td>
                  <td className="px-3 py-3 text-[#0B0F1A] font-semibold">{r.name}</td>
                  <td className="px-3 py-3"><span className="px-2 py-0.5 rounded-full bg-blue-50 text-blue-700 text-[10px] font-bold">{r.currency_code}</span></td>
                  <td className="px-3 py-3 text-[#5B617A]">{r.tax_label || "—"}</td>
                  <td className="px-3 py-3 tabular-nums text-[#0B0F1A]">{r.tax_rate}%</td>
                  <td className="px-3 py-3">
                    <div className="flex flex-wrap gap-1">
                      {(r.active_plan_slugs || []).map(s => (
                        <span key={s} className="px-1.5 py-0.5 rounded text-[9px] font-bold uppercase bg-[#7C5CFF]/10 text-[#7C5CFF]">{s}</span>
                      ))}
                    </div>
                  </td>
                  <td className="px-3 py-3">
                    {r.signup_enabled
                      ? <span className="text-emerald-600 text-xs font-bold">✓ Yes</span>
                      : <span className="text-red-500 text-xs font-bold">✗ No</span>}
                  </td>
                  <td className="px-3 py-3">
                    <button onClick={() => setEditing(r)} data-testid={`edit-country-${r.code}`}
                      className="flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs font-semibold text-[#7C5CFF] hover:bg-[#7C5CFF]/[0.06] transition-colors">
                      <Pencil size={12} /> Edit
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {editing && (
        <EditCountryModal
          row={editing}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }}
          toast={toast}
        />
      )}
    </div>
  );
}

function SourceBadge({ source }) {
  if (source === "override") return <span className="px-1.5 py-0.5 rounded text-[9px] font-bold bg-emerald-50 text-emerald-700">Override</span>;
  if (source === "mixed") return <span className="px-1.5 py-0.5 rounded text-[9px] font-bold bg-amber-50 text-amber-700">Mixed</span>;
  return <span className="px-1.5 py-0.5 rounded text-[9px] font-bold bg-slate-100 text-slate-500">Central</span>;
}

function EditCountryModal({ row, onClose, onSaved, toast }) {
  const [form, setForm] = useState({
    name: row.name,
    currency_code: row.currency_code,
    tax_label: row.tax_label || "",
    tax_rate: row.tax_rate,
    active_plan_slugs: [...(row.active_plan_slugs || [])],
    signup_enabled: row.signup_enabled,
  });
  const [plans, setPlans] = useState([]);
  const [saving, setSaving] = useState(false);
  // Price overrides state
  const [overrides, setOverrides] = useState({});        // { slug: {monthly:"", annual:""} }
  const [effectivePrices, setEffectivePrices] = useState(null); // from effective-prices endpoint
  const [touchedPlans, setTouchedPlans] = useState(new Set()); // only send touched
  const [clearedPlans, setClearedPlans] = useState(new Set()); // plans to null-delete

  useEffect(() => {
    api.get("/packages").then(r => {
      setPlans((r.data.items || []).map(p => p.slug));
    }).catch(() => {});
    // Fetch effective prices for placeholders + source badges
    api.get(`/country-settings/${row.code}/effective-prices`).then(r => {
      setEffectivePrices(r.data.effective_prices || {});
    }).catch(() => {});
  }, [row.code]);

  // Initialize overrides from existing price_overrides
  useEffect(() => {
    const po = row.price_overrides || {};
    const init = {};
    (row.active_plan_slugs || []).forEach(slug => {
      const ov = po[slug] || {};
      init[slug] = {
        monthly: ov.monthly != null ? String(ov.monthly) : "",
        annual: ov.annual != null ? String(ov.annual) : "",
      };
    });
    setOverrides(init);
  }, [row.price_overrides, row.active_plan_slugs]);

  const set = (k, v) => setForm(prev => ({ ...prev, [k]: v }));

  const setOv = (slug, field, val) => {
    setOverrides(prev => ({ ...prev, [slug]: { ...prev[slug], [field]: val } }));
    setTouchedPlans(prev => new Set(prev).add(slug));
    setClearedPlans(prev => { const n = new Set(prev); n.delete(slug); return n; });
  };

  const clearPlan = (slug) => {
    setOverrides(prev => ({ ...prev, [slug]: { monthly: "", annual: "" } }));
    setClearedPlans(prev => new Set(prev).add(slug));
    setTouchedPlans(prev => { const n = new Set(prev); n.delete(slug); return n; });
  };

  const togglePlan = (slug) => {
    set("active_plan_slugs",
      form.active_plan_slugs.includes(slug)
        ? form.active_plan_slugs.filter(s => s !== slug)
        : [...form.active_plan_slugs, slug]
    );
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = {
        name: form.name,
        currency_code: form.currency_code,
        tax_label: form.tax_label,
        tax_rate: Number(form.tax_rate),
        active_plan_slugs: form.active_plan_slugs,
        signup_enabled: form.signup_enabled,
      };
      // Build surgical price_overrides
      const poPatches = {};
      for (const slug of clearedPlans) {
        poPatches[slug] = null;
      }
      for (const slug of touchedPlans) {
        const ov = overrides[slug] || {};
        const origOv = (row.price_overrides || {})[slug] || {};
        const patch = {};
        if (ov.monthly !== "" && ov.monthly !== (origOv.monthly != null ? String(origOv.monthly) : "")) {
          patch.monthly = Number(ov.monthly);
        } else if (ov.monthly === "" && origOv.monthly != null) {
          patch.monthly = null;
        }
        if (ov.annual !== "" && ov.annual !== (origOv.annual != null ? String(origOv.annual) : "")) {
          patch.annual = Number(ov.annual);
        } else if (ov.annual === "" && origOv.annual != null) {
          patch.annual = null;
        }
        if (Object.keys(patch).length) poPatches[slug] = patch;
      }
      if (Object.keys(poPatches).length) payload.price_overrides = poPatches;
      await api.patch(`/country-settings/${row.code}`, payload);
      toast.success(`${row.code} settings updated`);
      onSaved();
    } catch (e) {
      const d = e?.response?.data?.detail;
      const msg = Array.isArray(d) ? d.map(x => x.msg || JSON.stringify(x)).join("; ") : (typeof d === "string" ? d : "Save failed");
      toast.error(msg);
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" onClick={onClose}>
      <div onClick={e => e.stopPropagation()}
        className="bg-white rounded-2xl shadow-xl border border-[#E2E8F0] w-full max-w-lg max-h-[85vh] overflow-y-auto p-6"
        data-testid="edit-country-modal">

        <div className="flex items-center justify-between mb-5">
          <h2 className="text-lg font-extrabold text-[#0B0F1A]">
            {COUNTRY_FLAGS[row.code] || "🌐"} Edit {row.code}
          </h2>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-[#F1F5F9] text-[#5B617A]"><X size={18} /></button>
        </div>

        <div className="space-y-4">
          <div>
            <label className="block text-[10px] font-bold text-[#5B617A] uppercase tracking-wider mb-1">Code (immutable)</label>
            <input value={row.code} disabled className="input-field opacity-60 cursor-not-allowed" data-testid="country-code-input" />
          </div>

          <div>
            <label className="block text-[10px] font-bold text-[#5B617A] uppercase tracking-wider mb-1">Name</label>
            <input value={form.name} onChange={e => set("name", e.target.value)}
              className="input-field" data-testid="country-name-input" />
          </div>

          <div>
            <label className="block text-[10px] font-bold text-[#5B617A] uppercase tracking-wider mb-1">Currency</label>
            <select value={form.currency_code} onChange={e => set("currency_code", e.target.value)}
              className="input-field" data-testid="country-currency-select">
              {SUPPORTED_CURRENCIES.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-[10px] font-bold text-[#5B617A] uppercase tracking-wider mb-1">Tax Label</label>
              <input value={form.tax_label} onChange={e => set("tax_label", e.target.value)}
                placeholder="e.g. GST, VAT, None"
                className="input-field" data-testid="country-tax-label-input" />
            </div>
            <div>
              <label className="block text-[10px] font-bold text-[#5B617A] uppercase tracking-wider mb-1">Tax Rate (%)</label>
              <input type="number" min="0" max="100" step="0.1"
                value={form.tax_rate} onChange={e => set("tax_rate", e.target.value)}
                className="input-field tabular-nums" data-testid="country-tax-rate-input" />
            </div>
          </div>

          <div>
            <label className="block text-[10px] font-bold text-[#5B617A] uppercase tracking-wider mb-1.5">Active Plans</label>
            <div className="flex flex-wrap gap-2" data-testid="country-plans-select">
              {plans.map(slug => {
                const on = form.active_plan_slugs.includes(slug);
                return (
                  <button key={slug} onClick={() => togglePlan(slug)} type="button"
                    data-testid={`country-plan-${slug}`}
                    className={`px-3 py-1.5 rounded-lg text-xs font-bold uppercase transition-colors border-2 ${
                      on ? "border-[#7C5CFF] bg-[#7C5CFF]/10 text-[#7C5CFF]" : "border-[#E2E8F0] text-[#5B617A] hover:border-[#CBD5E1]"
                    }`}
                  >
                    {on && <Check size={10} className="inline mr-1" />}{slug}
                  </button>
                );
              })}
            </div>
          </div>

          <div className="flex items-center justify-between">
            <label className="text-[10px] font-bold text-[#5B617A] uppercase tracking-wider">Signup Enabled</label>
            <button onClick={() => set("signup_enabled", !form.signup_enabled)} type="button"
              data-testid="country-signup-toggle"
              className="transition-colors">
              {form.signup_enabled
                ? <ToggleRight size={28} className="text-emerald-500" />
                : <ToggleLeft size={28} className="text-[#CBD5E1]" />}
            </button>
          </div>

          {/* Price Overrides */}
          <div data-testid="price-overrides-section">
            <label className="block text-[10px] font-bold text-[#5B617A] uppercase tracking-wider mb-2">Price Overrides ({form.currency_code})</label>
            <div className="border border-[#E2E8F0] rounded-xl overflow-hidden">
              <table className="w-full text-xs">
                <thead>
                  <tr className="bg-[#F8FAFC] text-[10px] font-semibold text-[#5B617A] uppercase tracking-wider">
                    <th className="px-2 py-1.5 text-left">Plan</th>
                    <th className="px-2 py-1.5 text-left">Monthly</th>
                    <th className="px-2 py-1.5 text-left">Annual</th>
                    <th className="px-2 py-1.5 w-8"></th>
                  </tr>
                </thead>
                <tbody>
                  {(form.active_plan_slugs || []).map(slug => {
                    const ov = overrides[slug] || { monthly: "", annual: "" };
                    const eff = effectivePrices?.[slug] || {};
                    return (
                      <tr key={slug} className="border-t border-[#F1F5F9]" data-testid={`price-row-${slug}`}>
                        <td className="px-2 py-2">
                          <div className="flex items-center gap-1.5">
                            <span className="font-bold text-[#0B0F1A] uppercase">{slug}</span>
                            <SourceBadge source={eff.source} />
                          </div>
                        </td>
                        <td className="px-2 py-2">
                          <input type="number" min="0" step="1"
                            value={ov.monthly} onChange={e => setOv(slug, "monthly", e.target.value)}
                            placeholder={eff.monthly != null ? `inherits ${eff.monthly}` : "—"}
                            data-testid={`price-monthly-${slug}`}
                            className="w-full px-2 py-1 rounded-lg border border-[#E2E8F0] text-xs tabular-nums focus:border-[#7C5CFF] focus:outline-none placeholder:text-[#CBD5E1]" />
                        </td>
                        <td className="px-2 py-2">
                          <input type="number" min="0" step="1"
                            value={ov.annual} onChange={e => setOv(slug, "annual", e.target.value)}
                            placeholder={eff.annual != null ? `inherits ${eff.annual}` : "—"}
                            data-testid={`price-annual-${slug}`}
                            className="w-full px-2 py-1 rounded-lg border border-[#E2E8F0] text-xs tabular-nums focus:border-[#7C5CFF] focus:outline-none placeholder:text-[#CBD5E1]" />
                        </td>
                        <td className="px-2 py-2 text-center">
                          <button type="button" onClick={() => clearPlan(slug)}
                            data-testid={`price-clear-${slug}`}
                            title="Clear override" className="p-1 rounded hover:bg-red-50 text-[#CBD5E1] hover:text-red-500 transition-colors">
                            <Trash2 size={12} />
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <p className="text-[10px] text-[#5B617A]/70 mt-1.5">Overrides are in {form.currency_code}. Changing the country's currency will NOT auto-convert these — re-enter if needed.</p>
          </div>

          <div className="flex gap-3 pt-2">
            <button onClick={onClose} className="flex-1 px-4 py-2.5 rounded-xl text-sm font-semibold text-[#5B617A] border border-[#E2E8F0] hover:bg-[#F1F5F9]">
              Cancel
            </button>
            <button onClick={handleSave} disabled={saving} data-testid="country-save-btn"
              className="flex-1 px-4 py-2.5 rounded-xl text-sm font-semibold text-white bg-[#7C5CFF] hover:bg-[#6A4DE0] disabled:opacity-50 transition-colors">
              {saving ? <Loader2 size={14} className="animate-spin mx-auto" /> : "Save"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
